import type { ApolloCache } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client';
import type {
  UpdateAssessmentInput,
  UpdateOversightAssessmentMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Assessment_Status_Enum,
  UpdateOversightAssessmentDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpdateOversightAssessmentTRPC } from './useUpdateOversightAssessmentTRPC';

/**
 * Apollo reads that this mutation invalidates.
 *
 * Shared by both branches on purpose. The migrated tRPC path still has to
 * evict these because the corresponding READS are NOT migrated and are
 * cache-first, so a write via tRPC otherwise leaves the Apollo-backed list or
 * counter showing pre-write data. Keeping one list means the two paths cannot
 * drift — that drift is what produced the stale oversight count.
 *
 * This lives in the WRAPPER, not in the `*TRPC` hook: the wrapper is the
 * flag-aware seam that already owns the Apollo mutation, so the tRPC hook
 * stays free of Apollo.
 */
const evictApolloReads = (cache: ApolloCache<unknown>) => {
  evictField(cache, 'risk_assessment_result');
  evictField(cache, 'document_assessment_result');
  evictField(cache, 'obligation_assessment_result');
  evictField(cache, 'oversight_assessment');
};

/**
 * The wrapper's public input: the legacy GraphQL input plus the
 * optimistic-concurrency token (COI-622), which rides only on the tRPC leg.
 */
type WrapperInput = UpdateAssessmentInput & {
  OriginalTimestamp?: string | null;
};

export const useUpdateOversightAssessment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const apolloClient = useApolloClient();

  // GraphQL mutation
  const [updateOversightAssessmentGraphQL, graphqlState] = useMutation(
    UpdateOversightAssessmentDocument,
    {
      update: (cache) => {
        evictApolloReads(cache);
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateOversightAssessmentTRPC();

  const updateOversightAssessment = async (
    variables: WrapperInput
  ): Promise<UpdateOversightAssessmentMutation> => {
    if (trpcEnabled) {
      const trpcResult = await trpcMutation.updateOversightAssessment({
        ...variables,
        Title: variables.Title ?? '',
        Status: variables.Status ?? Assessment_Status_Enum.Notstarted,
      });
      evictApolloReads(apolloClient.cache);

      return trpcResult;
    }

    const { OriginalTimestamp: _token, ...graphqlVariables } = variables;
    const result = await updateOversightAssessmentGraphQL({
      variables: { object: graphqlVariables },
    });
    if (!result.data) {
      throw new Error('Failed to update oversight assessment');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateOversightAssessment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateOversightAssessment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
